import { ProfileOutlined, ShopOutlined, UserOutlined } from '@ant-design/icons';
import { App, Button, Divider, Flex, message, Skeleton, Spin, StepProps, Steps, Alert } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ApplicationStatus, BtlApplication, HouseholdsToHasIndividuals } from '../../api/btlApplicationForm/types';
import { FeatureFlagEnum } from '../../common/feature-flags/enums/feature-flag.enum';
import { isFeatureEnabled } from '../../common/feature-flags/utils/is-feature-enabled.util';
import { FormValidationProvider } from '../../hooks';
import { useBtlApplicationInProgressById, useGetAffordabilityCheck, useRunAffordabilityCheck } from '../../queries';
import { useGetUser } from '../../queries';
import { getPageURLWithSection, PageURL } from '../../router/pageURL';
import type { MessageKeys } from '../../types/intl';
import { TypedFormatMessageFn } from '../../types/intl';
import { FormFooter } from './BtlBaseForm';
import { AffordabilityCheck } from './common/AffordabilityCheck';
import { ProductTitle } from './Product';
import { BtlApplicationFormPageEnum, BtlFormSectionParam, SectionProblems } from './types';
import { useBtlFormMappedRoutes } from './useBtlFormMappedRoutes';
import { useBtlMissingFields } from 'queries/btlApplications/useBtlMissingFields';
import { Suspense, useEffect } from 'react';
import { getIcon, getProblemsSubtitle } from './stepItems';
import {
  ALL_STEPS,
  getAdditionalInformationStepIndex,
  getDocumentsStepIndex,
  getPostOfferStepIndex,
  getValuationStepIndex,
  isApplicationSubmittedOrLater,
  isDipAcceptedOrLater,
  STATUS_TO_LABELS,
  STATUS_TO_STEPS,
} from './constants';
import { PermissionType } from '../../enums';
import { btlApplicationFormApi } from '../../api/btlApplicationForm';
import { ITEM_TITLE_KEYS, isNestedFormSection, resolveCaseSidebarItemId } from 'components/CaseSidebar';
import { useIsBtlCaseSidebarEnabled } from 'providers/PosthogProvider';
import { BtlCaseSidebar } from './BtlCaseSidebar';

const ApplicationActionsSidebar = ({ data }: { data?: BtlApplication }) => {
  const { formatMessage }: { formatMessage: TypedFormatMessageFn } = useIntl();
  const navigate = useNavigate();
  const updateSectionInUrl = (section: BtlFormSectionParam) => {
    navigate({
      search: `?section=${section}`,
    });
  };

  const { currentSection } = useBtlFormMappedRoutes(data);

  const dipStatus = STATUS_TO_LABELS[data?.status as keyof typeof STATUS_TO_LABELS] || '';

  return (
    <>
      <hr className='-mx-1 opacity-30 my-4' />
      <div>{formatMessage({ id: 'btlApplicationForm.status.label' })}</div>
      <h3 className={'mt-0'}>{dipStatus}</h3>
      <Flex vertical gap={12}>
        {currentSection === BtlApplicationFormPageEnum.CASE_SUMMARY && (
          <Button
            type={'primary'}
            disabled={data?.status !== 'DIP_ACCEPTED'}
            onClick={() => updateSectionInUrl(BtlApplicationFormPageEnum.FINANCING_AND_PROPERTY)}
          >
            {formatMessage({ id: 'btlApplicationForm.continueApplication.label' })}
          </Button>
        )}
      </Flex>
    </>
  );
};

const StepSidebar = ({
  id,
  data,
  isAffordabilityPassed,
  isLoading,
}: {
  id: string;
  data?: BtlApplication;
  isAffordabilityPassed?: boolean;
  isLoading: boolean;
}) => {
  const { formatMessage }: { formatMessage: TypedFormatMessageFn } = useIntl();
  const navigate = useNavigate();
  const { modal } = App.useApp();
  const { currentSection, previousSection, activeStepIndex } = useBtlFormMappedRoutes(data);
  const { data: missingFieldsRes, refetch: refetchMissingFields } = useBtlMissingFields(id);
  const { refetch: refetchAffordability } = useRunAffordabilityCheck(id);
  const { data: affordabilityData } = useGetAffordabilityCheck(id);

  const isAffordabilityRunRequired = data?.isAffordabilityRunRequired;
  const numberOfAffordabilityRuns = affordabilityData?.length ?? 0;
  const showAffordabilityWarning = numberOfAffordabilityRuns > 0;

  const missingFields = missingFieldsRes?.missingFields;
  const areMandatoryFieldsComplete = missingFieldsRes?.isComplete;
  const householdsToHasIndividuals = missingFieldsRes?.householdsToHasIndividuals;

  const disableTopSlicing = async () => {
    modal.confirm({
      title: formatMessage({ id: 'btlApplicationForm.TOP_SLICING.disableTopSlicingModal.title' }),
      content: (
        <FormattedMessage
          id={'btlApplicationForm.TOP_SLICING.disableTopSlicingModal.content'}
          values={{ br: <br /> }}
        />
      ),
      width: 600,
      okText: formatMessage({ id: 'btlApplicationForm.TOP_SLICING.disableTopSlicingModal.confirmText' }),
      onOk: async () => {
        await btlApplicationFormApi.toggleTopSlicing(id, false);
        await refetchAffordability();
        if (
          [BtlApplicationFormPageEnum.TOP_SLICING, BtlApplicationFormPageEnum.TOP_SLICING_HOUSEHOLD].includes(
            currentSection!,
          )
        ) {
          updateSectionInUrl(previousSection!);
        }
        message.success(formatMessage({ id: 'btlApplicationForm.TOP_SLICING.disableTopSlicingModal.successMessage' }));
      },
    });
  };

  useEffect(() => {
    refetchMissingFields();
  }, [activeStepIndex, navigate, data?.borrower?.individuals, data?.borrower?.companies]);

  const updateSectionInUrl = (section: BtlFormSectionParam) => {
    navigate({
      search: `?section=${section}`,
    });
  };
  const caseSummaryTitle =
    data?.title.split(' -')[0] !== data?.referenceNumber ? data?.title.split(' -')[0] : 'Case Summary';
  return (
    <div
      className={'min-w-[248px] shrink-0 bg-gradient-to-b from-white to-gray-100 rounded-xl p-4 flex flex-col h-full'}
    >
      {id ? (
        <div
          onClick={() =>
            navigate(
              getPageURLWithSection(
                PageURL.BTL_APPLICATION_EDIT,
                { id },
                { section: BtlApplicationFormPageEnum.CASE_SUMMARY },
              ),
            )
          }
          className={`block bg-gray-50 rounded p-2 -m-2 mb-4 pb-[1px] transition !outline !outline-1 !outline-transparent shrink-0 ${
            activeStepIndex?.() === undefined
              ? 'bg-gray-100 !outline-green-700 cursor-default'
              : 'hover:bg-gray-100 hover:!outline-green-700 cursor-pointer'
          }`}
        >
          <Skeleton title={false} loading={isLoading} active paragraph={{ style: { marginTop: 2 }, rows: 2 }}>
            <div className={'flex gap-2'}>
              <ProfileOutlined style={{ fontSize: 24, alignSelf: 'start' }} />
              <div>
                <h3 className={'my-0'}>{caseSummaryTitle}</h3>
                <div className={'font-normal mb-2'}>{data?.referenceNumber}</div>
              </div>
            </div>
          </Skeleton>
        </div>
      ) : (
        <h3 className={'mt-0 mb-8 shrink-0'}>{formatMessage({ id: 'btlApplicationForm.createCase.label' })}</h3>
      )}
      <div className={'custom-scrollbar min-h-0 flex-1 overflow-y-auto'}>
        <Skeleton loading={isLoading} paragraph={{ rows: 6 }} active>
          <Steps
            direction='vertical'
            current={activeStepIndex?.()}
            items={stepItems(
              formatMessage,
              data,
              activeStepIndex?.(),
              updateSectionInUrl,
              isAffordabilityPassed,
              missingFields,
              areMandatoryFieldsComplete,
              householdsToHasIndividuals,
              isAffordabilityRunRequired && showAffordabilityWarning,
            )}
            onChange={() => {
              /* the onChange (even though it's blank) is required to keep the Antd hover effect */
            }}
            /*progressDot={(iconDot, info) => {
              switch (info.status) {
                case 'wait':
                  return <div className={'border-[#004615]/50 border-solid border-1 rounded-full h-6 w-6 -mx-2 -mt-[6px]'} />;
                case 'process':
                  return <CheckCircleFilled style={{ fontSize: 24, color: '#004615' }} className={'-mx-2 -mt-[5px]'} />;
                case 'finish':
                  return <CheckCircleOutlined style={{ fontSize: 24 }} className={'-mx-2 -mt-[6px]'} />;
              }
            }}*/
          />
        </Skeleton>
      </div>
      <div className={'shrink-0'}>
        <Skeleton loading={isLoading} paragraph={{ rows: 1 }} active>
          {data?.status && data?.status !== 'NEW' && <ApplicationActionsSidebar data={data} />}

          {data?.isTopSlicing && (
            <Button
              className={'w-full mt-4'}
              size={'middle'}
              color={'default'}
              variant={'filled'}
              onClick={disableTopSlicing}
            >
              {formatMessage({ id: 'btlApplicationForm.TOP_SLICING.disableTopSlicing.actionText' })}
            </Button>
          )}
        </Skeleton>
      </div>
    </div>
  );
};

// Note: exported so it can be unit tested
export const stepItems = (
  formatMessage: TypedFormatMessageFn,
  data?: BtlApplication,
  activeIndex?: number,
  updateSectionInUrl?: (section: BtlApplicationFormPageEnum) => void,
  isAffordabilityPassed?: boolean,
  problems?: SectionProblems,
  areMandatoryFieldsComplete?: boolean,
  householdsToHasIndividuals?: HouseholdsToHasIndividuals,
  isAffordabilityRunRequired?: boolean,
): StepProps[] => {
  const purpose = data?.purpose ? data.purpose.toLowerCase() : null;
  const ftv = data?.netFinancingFtv ? `FTV: ${((data?.netFinancingFtv ?? 0) * 100).toFixed(0)}%` : null;
  const applicantCompanies = data?.borrower?.companies.length ?? 0;
  const applicantIndividuals = data?.borrower?.individuals.length ?? 0;
  const applicantDescriptions = [
    applicantCompanies && (
      <span key='companies' data-testid={'companies'}>
        <ShopOutlined /> {applicantCompanies}
      </span>
    ),
    applicantCompanies && applicantIndividuals && ' / ',
    applicantIndividuals && (
      <span key='individuals' data-testid={'individuals'}>
        <UserOutlined /> {applicantIndividuals}
      </span>
    ),
  ].filter(Boolean);

  const showStepAtStatus = (status: string | undefined, page: BtlApplicationFormPageEnum) => {
    if (!status) return false;
    const steps = STATUS_TO_STEPS[status as keyof typeof STATUS_TO_STEPS];
    return steps?.includes(page);
  };

  // annoying gotcha here to make sure that on DIP_ACCEPTED status, we don't unlock the
  // last steps with this functionality, as instead we need to depend on whether the first 3 steps are valid
  const canAccessAllSteps =
    STATUS_TO_STEPS[data?.status as keyof typeof STATUS_TO_STEPS] === ALL_STEPS &&
    data?.status !== ApplicationStatus.DIP_ACCEPTED;

  return [
    {
      title: formatMessage({ id: 'btlApplicationForm.FINANCING_AND_PROPERTY.label' }),
      subTitle: getProblemsSubtitle(BtlApplicationFormPageEnum.FINANCING_AND_PROPERTY, problems, formatMessage),
      description: (purpose || ftv) && `${purpose} / ${ftv}`,
      status: data?.id && activeIndex !== 0 ? 'finish' : 'process',
      icon: getIcon(activeIndex, 0, BtlApplicationFormPageEnum.FINANCING_AND_PROPERTY, problems),
      className: 'cursor-pointer capitalize',
      onClick: () => updateSectionInUrl?.(BtlApplicationFormPageEnum.FINANCING_AND_PROPERTY),
    },
    {
      title: formatMessage({ id: 'btlApplicationForm.APPLICANTS.label' }),
      subTitle: [
        getProblemsSubtitle(
          BtlApplicationFormPageEnum.APPLICANTS_INDIVIDUAL,
          problems,
          formatMessage,
          data?.borrower?.individuals.map(ind => `${ind.firstName} ${ind.lastName}`),
        ),
      ].concat(
        getProblemsSubtitle(
          BtlApplicationFormPageEnum.APPLICANTS_COMPANY,
          problems,
          formatMessage,
          data?.borrower?.companies.map(ind => ind.companyName),
        ),
      ),
      description: applicantDescriptions,
      disabled: !data?.id,
      status:
        applicantCompanies + applicantIndividuals > 0 && activeIndex !== 1
          ? 'finish'
          : applicantCompanies + applicantIndividuals === 0 && activeIndex !== 1
          ? 'wait'
          : 'process',
      icon: getIcon(activeIndex, 1, BtlApplicationFormPageEnum.APPLICANTS, problems),
      style: {
        transition: 'unset',
      },
      className: data?.id ? 'cursor-pointer' : undefined,
      onClick: () => data?.id && updateSectionInUrl?.(BtlApplicationFormPageEnum.APPLICANTS),
    },
    {
      title: formatMessage({ id: 'btlApplicationForm.PRODUCT.label' }),
      subTitle: data?.product ? getProblemsSubtitle(BtlApplicationFormPageEnum.PRODUCT, problems, formatMessage) : null,
      description: data?.product && (
        <ProductTitle
          maximumFTV={data.product.maximumFTV}
          monthsInitial={data.product.monthsInitial}
          productType={data.product.productType}
        />
      ),
      disabled: applicantCompanies + applicantIndividuals === 0,
      status: data?.product && activeIndex !== 2 ? 'finish' : !data?.product && activeIndex !== 2 ? 'wait' : 'process',
      icon: getIcon(activeIndex, 2, BtlApplicationFormPageEnum.PRODUCT, !data?.product ? undefined : problems),
      style: {
        transition: 'unset',
      },
      className: applicantCompanies + applicantIndividuals !== 0 ? 'cursor-pointer' : undefined,
      onClick: () =>
        applicantCompanies + applicantIndividuals !== 0 && updateSectionInUrl?.(BtlApplicationFormPageEnum.PRODUCT),
    },
    ...(data?.isTopSlicing === true && showStepAtStatus(data?.status, BtlApplicationFormPageEnum.TOP_SLICING)
      ? [
          {
            title: formatMessage({ id: 'btlApplicationForm.TOP_SLICING.label' }),
            disabled: !data?.product,
            className: data?.product ? 'cursor-pointer' : undefined,
            subTitle: getProblemsSubtitle(
              BtlApplicationFormPageEnum.TOP_SLICING,
              !data?.product ? undefined : problems,
              formatMessage,
              undefined,
              householdsToHasIndividuals,
            ),
            icon: getIcon(
              activeIndex,
              3,
              BtlApplicationFormPageEnum.TOP_SLICING,
              !data?.product ? undefined : problems,
              undefined,
              undefined,
              householdsToHasIndividuals,
            ),
            style: {
              transition: 'unset',
            },
            onClick: () => data?.product && updateSectionInUrl?.(BtlApplicationFormPageEnum.TOP_SLICING),
          },
        ]
      : []),
    ...(showStepAtStatus(data?.status, BtlApplicationFormPageEnum.AFFORDABILITY_CHECK) || data === null // these null checks are for a brand new application, before there is any data on the backend
      ? [
          {
            title: formatMessage({ id: 'btlApplicationForm.AFFORDABILITY_CHECK.label' }),
            disabled: !data?.product,
            className: data?.product ? 'cursor-pointer' : undefined,
            subTitle: getProblemsSubtitle(
              BtlApplicationFormPageEnum.AFFORDABILITY_CHECK,
              !data?.product ? undefined : problems,
              formatMessage,
            ),
            icon: getIcon(
              activeIndex,
              data?.isTopSlicing ? 4 : 3,
              BtlApplicationFormPageEnum.AFFORDABILITY_CHECK,
              !data?.product ? undefined : problems,
              undefined,
              undefined,
              undefined,
              isAffordabilityRunRequired,
            ),
            style: {
              transition: 'unset',
            },
            onClick: () => data?.product && updateSectionInUrl?.(BtlApplicationFormPageEnum.AFFORDABILITY_CHECK),
          },
        ]
      : []),
    ...(showStepAtStatus(data?.status, BtlApplicationFormPageEnum.SUBMIT_DIP) || data === null
      ? [
          {
            title: formatMessage({ id: 'btlApplicationForm.SUBMIT_DIP.label' }),
            disabled: !isAffordabilityPassed,
            className: data?.product ? 'cursor-pointer' : undefined,
            subTitle: getProblemsSubtitle(
              BtlApplicationFormPageEnum.SUBMIT_DIP,
              !isAffordabilityPassed ? undefined : problems,
              formatMessage,
            ),
            icon: getIcon(
              activeIndex,
              data?.isTopSlicing ? 5 : 4,
              BtlApplicationFormPageEnum.SUBMIT_DIP,
              !isAffordabilityPassed ? undefined : problems,
            ),
            style: {
              transition: 'unset',
            },
            onClick: () => isAffordabilityPassed && updateSectionInUrl?.(BtlApplicationFormPageEnum.SUBMIT_DIP),
          },
        ]
      : []),
    ...[
      {
        key: BtlApplicationFormPageEnum.SOLICITORS,
        index: data?.isTopSlicing ? 5 : 4,
      },
      {
        key: BtlApplicationFormPageEnum.BANK_DETAILS,
        index: data?.isTopSlicing ? 6 : 5,
      },
      ...(isApplicationSubmittedOrLater(data?.status)
        ? [
            {
              key: BtlApplicationFormPageEnum.COMMUNICATION,
              index: data?.isTopSlicing ? 7 : 6,
            },
          ]
        : []),
      ...(isDipAcceptedOrLater(data?.status)
        ? [
            {
              key: BtlApplicationFormPageEnum.DOCUMENTS,
              index: getDocumentsStepIndex(data?.status, data?.isTopSlicing) ?? (data?.isTopSlicing ? 8 : 7),
            },
          ]
        : []),
      ...(showStepAtStatus(data?.status, BtlApplicationFormPageEnum.VALUATION)
        ? [
            {
              key: BtlApplicationFormPageEnum.VALUATION,
              index: getValuationStepIndex(data?.status, data?.isTopSlicing) ?? (data?.isTopSlicing ? 8 : 7),
            },
          ]
        : []),
      ...(showStepAtStatus(data?.status, BtlApplicationFormPageEnum.POST_OFFER)
        ? [
            {
              key: BtlApplicationFormPageEnum.POST_OFFER,
              index: getPostOfferStepIndex(data?.status, data?.isTopSlicing) ?? (data?.isTopSlicing ? 9 : 8),
            },
          ]
        : []),
      {
        key: BtlApplicationFormPageEnum.ADDITIONAL_INFORMATION,
        index: getAdditionalInformationStepIndex(data?.status, data?.isTopSlicing) ?? (data?.isTopSlicing ? 10 : 9),
      },
    ].flatMap(({ key, index }) =>
      showStepAtStatus(data?.status, key)
        ? [
            {
              title: formatMessage({ id: `btlApplicationForm.${key}.label` as MessageKeys }),
              disabled: (!isAffordabilityPassed || !areMandatoryFieldsComplete) && !canAccessAllSteps,
              subTitle: getProblemsSubtitle(key, !isAffordabilityPassed ? undefined : problems, formatMessage),
              icon: getIcon(
                activeIndex,
                index,
                key,
                !isAffordabilityPassed ? undefined : problems,
                null,
                areMandatoryFieldsComplete,
              ),
              style: { transition: 'unset' },
              onClick: () => {
                if ((isAffordabilityPassed && areMandatoryFieldsComplete) || canAccessAllSteps) {
                  updateSectionInUrl?.(key);
                }
              },
            },
          ]
        : [],
    ),
  ];
};

export const BtlApplicationFormPage = () => {
  const { formatMessage }: { formatMessage: TypedFormatMessageFn } = useIntl();
  const { modal } = App.useApp();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const formSectionParam = searchParams.get('section');
  const formSection = formSectionParam as BtlApplicationFormPageEnum;
  const { id = '' } = useParams();
  const { isLoading, data } = useBtlApplicationInProgressById(id);
  const { data: dataRuns } = useGetAffordabilityCheck(id);
  const { refetch: refetchAffordability } = useRunAffordabilityCheck(id);
  const isCaseSidebarEnabled = useIsBtlCaseSidebarEnabled();

  const affordabilityData = dataRuns?.[dataRuns?.length - 1 || 0]?.buyToLetOutput;

  const { FormPage, activeStepIndex, showStandaloneFormFooter, previousSection } = useBtlFormMappedRoutes(data);
  const shouldReserveFooterSpace =
    showStandaloneFormFooter || isCaseSidebarEnabled || activeStepIndex?.() !== undefined;

  const { data: user } = useGetUser();

  const userGroups = user?.roles.map(r => r.role.type) ?? [];

  const resolvedCaseSidebarItemId = resolveCaseSidebarItemId(formSectionParam);
  const formSectionLabelId: MessageKeys = isNestedFormSection(formSectionParam)
    ? (`btlApplicationForm.${formSection}.label` as MessageKeys)
    : resolvedCaseSidebarItemId
    ? ITEM_TITLE_KEYS[resolvedCaseSidebarItemId]
    : (`btlApplicationForm.${formSection}.label` as MessageKeys);

  const disableTopSlicing = async () => {
    modal.confirm({
      title: formatMessage({ id: 'btlApplicationForm.TOP_SLICING.disableTopSlicingModal.title' }),
      content: (
        <FormattedMessage
          id={'btlApplicationForm.TOP_SLICING.disableTopSlicingModal.content'}
          values={{ br: <br /> }}
        />
      ),
      width: 600,
      okText: formatMessage({ id: 'btlApplicationForm.TOP_SLICING.disableTopSlicingModal.confirmText' }),
      onOk: async () => {
        await btlApplicationFormApi.toggleTopSlicing(id, false);
        await refetchAffordability();
        if (
          [BtlApplicationFormPageEnum.TOP_SLICING, BtlApplicationFormPageEnum.TOP_SLICING_HOUSEHOLD].includes(
            formSection,
          ) &&
          previousSection
        ) {
          navigate({ search: `?section=${previousSection}` });
        }
        message.success(formatMessage({ id: 'btlApplicationForm.TOP_SLICING.disableTopSlicingModal.successMessage' }));
      },
    });
  };

  if (!isFeatureEnabled(FeatureFlagEnum.BTL_APPLICATION) && !userGroups?.includes(PermissionType.BROKER_SALES)) {
    return (
      <div className={'mx-auto max-w-screen-md text-center mt-16'}>
        {formatMessage({ id: 'btlApplicationForm.featureFlagNotEnabled.label' })}
      </div>
    );
  }

  return (
    <div className={'-mt-16 flex h-full min-h-0 bg-gray-100'}>
      <div
        className={
          isCaseSidebarEnabled ? 'flex h-full min-h-0 shrink-0' : 'flex h-full min-h-0 shrink-0 pl-6 pt-6 pb-4'
        }
      >
        {isCaseSidebarEnabled ? (
          <BtlCaseSidebar
            data={data}
            isLoading={isLoading}
            isAffordabilityPassed={affordabilityData?.isPassed}
            isAffordabilityRunRequired={data?.isAffordabilityRunRequired}
            currentSection={formSectionParam ?? undefined}
            onDisableTopSlicing={disableTopSlicing}
          />
        ) : (
          <StepSidebar id={id} data={data} isLoading={isLoading} isAffordabilityPassed={affordabilityData?.isPassed} />
        )}
      </div>
      <div
        className={
          isCaseSidebarEnabled ? 'flex min-h-0 flex-1 gap-8 p-6 pb-4 pl-8' : 'flex min-h-0 flex-1 gap-8 p-6 pb-4'
        }
      >
        <div className={'flex grow flex-col h-full relative pt-4'}>
          {(formSection === BtlApplicationFormPageEnum.SUBMIT_DIP ||
            formSection === BtlApplicationFormPageEnum.ADDITIONAL_INFORMATION) &&
            data?.isAffordabilityRunRequired && (
              <Alert
                className='mb-4 font-semibold'
                type='warning'
                showIcon
                description={formatMessage({ id: 'btlApplicationForm.AFFORDABILITY_CHECK.invalidated.label' })}
              />
            )}
          {formSection && (
            <h2 className={`mt-0 ${activeStepIndex?.() !== undefined ? 'mb-0' : ''}`}>
              <Skeleton loading={isLoading} paragraph={{ rows: 1 }} title={false} active className={'-mb-3'}>
                <FormattedMessage id={formSectionLabelId} values={{ title: data?.title }} />
              </Skeleton>
            </h2>
          )}
          {activeStepIndex?.() !== undefined && <Divider className={'my-4'} style={{ borderColor: '#BBB' }} />}
          <FormValidationProvider>
            <div
              id='form-scroll-container'
              className={`overflow-auto grow pt-2 pr-4 pb-4 ${
                activeStepIndex?.() !== undefined || isCaseSidebarEnabled ? 'pl-2' : ''
              } ${shouldReserveFooterSpace ? 'mb-16' : ''}`}
            >
              {FormPage ? (
                <Suspense fallback={<Spin size='large' className='flex justify-center py-16' />}>
                  <FormPage />
                </Suspense>
              ) : null}
            </div>
            {showStandaloneFormFooter && <FormFooter />}
          </FormValidationProvider>
        </div>
        {activeStepIndex?.() === undefined && (
          <div className={'w-[300px] shrink-0 bg-gradient-to-b from-white to-gray-100 rounded-xl p-4'}>
            <h3 className={'mt-0'}>{formatMessage({ id: 'btlApplicationForm.CASE_SUMMARY.affordability.label' })}</h3>
            <AffordabilityCheck showTopSlicingEligibility={true} />
          </div>
        )}
      </div>
    </div>
  );
};
